import React, { useState, useEffect, useRef } from "react";
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup ,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile
} from "firebase/auth";
import { useTranslation } from "react-i18next";
import { useConsent } from "./context/ConsentContext";
import { googleProvider, microsoftProvider } from "./firebase";
import { useNavigate, useLocation } from "react-router-dom";
import { track } from "./analytics/tracker";
import { EV } from "./analytics/events";


const Login = ({ user, setUser, customClass = "" }) => {
  const auth = getAuth();
  const { t } = useTranslation();
  const { decision, openSettings, getConsent } = useConsent();
  const { consent_analytics, consent_marketing } = getConsent();
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const [showConsentPrompt, setShowConsentPrompt] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
const nextPath = new URLSearchParams(location.search).get("next") || "/";

const finishLogin = () => {
  handleClose();
  navigate(nextPath, { replace: true });
};


const handleLoginClick = () => {
  if (!decision) {
    // umesto acceptAll, otvori settings da korisnik izabere
    openSettings();
    return;
  }
  setShowModal(true);
};


  const handleClose = () => {
    setShowModal(false);
    setIsRegistering(false);
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setShowPassword(false);
    setIsLoading(false);
  };

  const validateForm = () => {
    if (isRegistering && (!firstName || !lastName)) {
      alert(t("login.name_required"));
      return false;
    }
    if (!email.includes('@')) {
      alert(t("login.invalid_email"));
      return false;
    }
    if (password.length < 6) {
      alert(t("login.password_length"));
      return false;
    }
    return true;
  };

  const sendUserToBackend = async (firebaseUser) => {
    try {
      await fetch("https://modularobjects.com/checkout/api/user/firebase-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          lastLoginAt: firebaseUser.metadata?.lastSignInTime || new Date().toISOString()
        })
      });
    } catch (err) {
      console.error("❌ Backend user save error:", err);
    }
  };



  const loginWithProvider = async (provider, method = "oauth") => {
  try {
    const result = await signInWithPopup(auth, provider);

    setUser(result.user);
    await sendUserToBackend(result.user);

    await track(EV.LOGIN_SUCCESS, {
      firebase_uid: result.user?.uid || null,
      metadata: { method }
    });

    finishLogin();
  } catch (error) {
    console.error("❌ Full error:", error);
    alert("An error occurred during login. Please try again.");
  }
};

  const loginWithApple = () => alert(t("login.apple_not_implemented"));

  const registerWithEmail = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
await updateProfile(cred.user, { displayName: `${firstName} ${lastName}` });

const updatedUser = { ...cred.user, displayName: `${firstName} ${lastName}` };
setUser(updatedUser);
await sendUserToBackend(updatedUser);

await track(EV.LOGIN_SUCCESS, {
  firebase_uid: cred.user?.uid || null,
  metadata: { method: "email_register" }
});

finishLogin();
alert(t("login.registration_success"));
handleClose();
    } catch (error) {
      console.error("Registration error:", error);
      const errorMessage =
        error.code === "auth/email-already-in-use" ? t("login.email_in_use") :
        error.code === "auth/weak-password" ? t("login.weak_password") :
        t("login.register_error");
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithEmail = async () => {
  if (!email || !password) {
    alert(t("login.fill_all_fields"));
    return;
  }
  setIsLoading(true);
  try {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    setUser(cred.user);
    await sendUserToBackend(cred.user);

    await track(EV.LOGIN_SUCCESS, {
      firebase_uid: cred.user?.uid || null,
      metadata: { method: "email" }
    });

    finishLogin();
    handleClose();
  } catch (error) {
    console.error("Email/password login error:", error);
    alert(t("login.error"));
  } finally {
    setIsLoading(false);
  }
};

  const handleLogout = async () => {
  setIsLoading(true);
  try {
    await signOut(auth); // Poziva se Firebase logout
    setUser(null); // Resetovanje korisničkog stanja
    navigate("/"); // Preusmeravanje na početnu stranicu (ili na stranicu po tvom izboru)
    setShowModal(false); // Zatvaranje modal prozora kada se izloguješ
  } catch (error) {
    console.error("Error during sign out:", error);
  } finally {
    setIsLoading(false);
  }
};






  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && handleClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (window.location.pathname === "/login") {
      setShowModal(true);
    }
  }, []);

  const onOverlayClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      handleClose();
    }
  };

  return (
    <div className="inline-flex items-center">
      {!user ? (
        <>
          <button
  onClick={handleLoginClick}
  disabled={!decision}
  className={`${customClass || "btn-primary"} ${!decision ? "opacity-50 cursor-not-allowed" : ""}`}
  title={!decision ? (t("cookie.cookies_required_message") || "Please choose cookie preferences first.") : ""}
>
  🔐 {isRegistering ? t("login.register") : t("login.sign_in")}
</button>


          {showModal && (
            <div
              className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
              onClick={onOverlayClick}
            >
              <div
                ref={modalRef}
                className="bg-white dark:bg-neutral-900 p-6 rounded shadow-md w-96 mx-auto text-center"
              >
                <h2 className="text-lg font-semibold mb-4 text-text-light dark:text-text-dark">
                  {isRegistering ? t("login.register_title") : t("login.title")}
                </h2>

                {isRegistering && (
                  <>
                    <input
                      type="text"
                      placeholder={t("login.first_name")}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full border px-2 py-1 rounded mb-2 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark placeholder:text-gray-400"
                      autoFocus
                    />
                    <input
                      type="text"
                      placeholder={t("login.last_name")}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full border px-2 py-1 rounded mb-2 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark placeholder:text-gray-400"
                    />
                  </>
                )}

                <input
                  type="email"
                  placeholder={t("login.email_placeholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border px-2 py-1 rounded mb-2 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark placeholder:text-gray-400"
                  autoFocus={!isRegistering}
                />

                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder={t("login.password_placeholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full border px-2 py-1 rounded mb-2 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark placeholder:text-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute top-2 right-3 text-xs text-gray-500 dark:text-gray-300 hover:underline"
                  >
                    {showPassword ? t("login.hide_password") : t("login.show_password")}
                  </button>
                </div>

                {isLoading ? (
                  <div className="py-4 flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-light dark:border-primary-dark"></div>
                  </div>
                ) : (
                  <>
                    {isRegistering ? (
                      <button
                        onClick={registerWithEmail}
                        className="bg-primary-light dark:bg-primary-dark text-text-dark dark:text-text-light w-full py-1 rounded mb-4 font-semibold"
                      >
                        {t("login.register")}
                      </button>
                    ) : (
                      <button
                        onClick={loginWithEmail}
                        className="bg-primary-light dark:bg-primary-dark text-text-dark dark:text-text-light w-full py-1 rounded mb-4 font-semibold"
                      >
                        {t("login.continue")}
                      </button>
                    )}

                    {!isRegistering && (
                      <>
                        <button
 onClick={() => loginWithProvider(googleProvider, "google")}
  className="w-full flex items-center justify-center gap-2 border py-1 rounded mb-2 text-text-light dark:text-text-dark"
>
  <img
    src="https://modularobjects.com/svg/google-icon-logo-svgrepo-com.svg"
    alt="Google"
    className="h-5 w-5"
  />
  {t("login.google")}
</button>
                        <button
 onClick={() => loginWithProvider(microsoftProvider, "microsoft")}
  className="w-full flex items-center justify-center gap-2 border py-1 rounded mb-2 text-text-light dark:text-text-dark"
>
  <img
    src="https://modularobjects.com/svg/microsoft-svgrepo-com.svg"
    alt="Microsoft"
    className="h-5 w-5"
  />
  {t("login.microsoft")}
</button>
                        <button
                          onClick={loginWithApple}
                          className="w-full flex items-center justify-center gap-2 border py-2 rounded mb-2 text-text-light dark:text-text-dark"
                        >
                          <img
                            src="https://modularobjects.com/svg/apple-black-logo-svgrepo-com.svg"
                            alt="Apple"
                            className="h-5 w-5"
                          />
                          {t("login.apple")}
                        </button>
                        <p
                          className="mt-2 text-sm text-primary-light dark:text-primary-dark cursor-pointer hover:underline"
                          onClick={() => setIsRegistering(true)}
                        >
                          {t("login.create_account")}
                        </p>
                      </>
                    )}

                    {isRegistering && (
                      <p
                        className="mt-2 text-sm text-primary-light dark:text-primary-dark cursor-pointer hover:underline"
                        onClick={() => setIsRegistering(false)}
                      >
                        {t("login.have_account")}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="flex items-center space-x-2 text-sm">
          <span className="btn-primary">
            👤 {user.displayName}
          </span>
          <button
            onClick={handleLogout}
            disabled={isLoading}
            className={`${customClass || "btn-primary"} ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {isLoading ? t("login.logging_out") : t("login.logout")}
          </button>
        </div>
      )}

{showConsentPrompt && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 shadow-xl max-w-sm w-full text-center">
      <h2 className="text-xl font-semibold mb-4">
        {t("cookie.cookies_required_title") || "Privacy settings"}
      </h2>
      <p className="text-sm mb-6">
        {t("cookie.cookies_required_message") || "You can customize cookies anytime."}
      </p>

      <div className="flex gap-3 justify-center">
        <button
          onClick={() => {
            setShowConsentPrompt(false);
            openSettings(); // ✅ otvori settings
          }}
          className="btn-primary"
        >
          {t("cookie.settings", "Cookie settings")}
        </button>

        <button
          onClick={() => setShowConsentPrompt(false)}
          className="px-4 py-2 rounded border"
        >
          {t("cookie.close", "Close")}
        </button>
      </div>
    </div>
  </div>
)}

      
    </div>



  );
};

export default Login;
